import {
  ClaimsApi,
  Configuration,
  DocumentsApi,
  FetchError,
  ResponseError,
  type ClaimResponse,
  type DocumentInfo,
} from '@api-sdk'
import { ClaimSearchResult } from '../types/claim'

// Configuration constants
const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'https://localhost:7001',
} as const

// Error messages
const ERROR_MESSAGES = {
  UNAUTHORIZED: 'Unauthorized: Please re-authenticate.',
  BAD_REQUEST: 'Bad request: Please provide a search query.',
  SEARCH_FAILED: (status: number) => `Search failed with status ${status}`,
  REQUEST_FAILED: (status: number) => `Request failed with status ${status}`,
} as const

// HTTP status codes
const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  BAD_REQUEST: 400,
} as const

const TIMEOUT_MS = 60000 // 60 seconds

export class ApiUnauthorizedError extends Error {
  constructor(message: string = ERROR_MESSAGES.UNAUTHORIZED) {
    super(message)
    this.name = 'ApiUnauthorizedError'
  }
}

export class ApiTimeoutError extends Error {
  constructor(message: string = 'Request timed out. Please check your connection and try again.') {
    super(message)
    this.name = 'ApiTimeoutError'
  }
}

export class ApiNetworkError extends Error {
  constructor(message: string = 'Network error. Please check your connection and try again.') {
    super(message)
    this.name = 'ApiNetworkError'
  }
}

/**
 * Creates an AbortSignal that times out after TIMEOUT_MS.
 * Merges with an optional external signal.
 */
function createTimeoutSignal(externalSignal?: AbortSignal): AbortSignal {
  const signals: AbortSignal[] = [AbortSignal.timeout(TIMEOUT_MS)]
  if (externalSignal) signals.push(externalSignal)
  return AbortSignal.any(signals)
}

/**
 * Create API configuration with access token
 */
function createApiConfig(token: string): Configuration {
  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return new Configuration({
    basePath: API_CONFIG.BASE_URL,
    headers,
  })
}

function handleResponseError(error: ResponseError): never {
  const status = error.response.status
  if (status === HTTP_STATUS.UNAUTHORIZED) {
    throw new ApiUnauthorizedError()
  }
  if (status === HTTP_STATUS.BAD_REQUEST) {
    throw new Error(ERROR_MESSAGES.BAD_REQUEST)
  }
  throw new Error(ERROR_MESSAGES.SEARCH_FAILED(status))
}

function handleError(error: unknown): never {
  if (error instanceof ResponseError) {
    return handleResponseError(error)
  }
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    throw new ApiTimeoutError()
  }
  if (error instanceof FetchError) {
    throw new ApiNetworkError()
  }
  throw error
}

/**
 * Search claims by query string
 */
export async function searchClaims(
  query: string,
  searchType: string,
  token: string,
  signal?: AbortSignal
): Promise<ClaimSearchResult[]> {
  const config = createApiConfig(token)
  const api = new ClaimsApi(config)

  try {
    const fullQuery = searchType ? `${searchType}:${query}` : query
    const timeoutSignal = createTimeoutSignal(signal)
    const response = await api.searchClaimsRaw({ query: fullQuery }, { signal: timeoutSignal })
    const data = (await response.raw.json()) as ClaimSearchResult[]
    return data.map((claim) => ({
      ...claim,
      claimTitle: claim.claimTitle || claim.lossLocation || claim.insured || claim.claimNumber,
    }))
  } catch (error) {
    handleError(error)
  }
}

interface RecentClaimResponse {
  claimNumber: string
  primaryClient: string | null
  insured: string | null
  broker: string | null
  dateOfLoss: string
  lastAccessedAt: string
  lossLocation: string | null
}

function mapRecentClaim(claim: RecentClaimResponse): ClaimSearchResult {
  return {
    claimNumber: claim.claimNumber,
    claimTitle: claim.lossLocation || claim.insured || claim.claimNumber,
    insured: claim.insured || '',
    primaryClient: claim.primaryClient || '',
    policyType: '',
    presentPosition: '',
    dateOfLoss: claim.dateOfLoss,
    broker: claim.broker || '',
    totalReserves: 0,
    currency: '',
    lossLocation: null,
  }
}

/**
 * Get recent claims
 */
export async function recentClaims(token: string): Promise<ClaimSearchResult[]> {
  const config = createApiConfig(token)
  const api = new ClaimsApi(config)

  try {
    const timeoutSignal = createTimeoutSignal()
    const response = await api.getRecentClaimsRaw({ signal: timeoutSignal })
    const data = (await response.raw.json()) as RecentClaimResponse[]
    return data.map(mapRecentClaim)
  } catch (error) {
    handleError(error)
  }
}

function createApi(token: string) {
  return new ClaimsApi(createApiConfig(token))
}

function createDocumentsApi(token: string) {
  return new DocumentsApi(createApiConfig(token))
}

async function apiCall<T>(fn: () => Promise<{ raw: Response }>): Promise<T> {
  try {
    const response = await fn()
    return (await response.raw.json()) as T
  } catch (error) {
    return handleError(error)
  }
}

export async function getClaim(claimNumber: string, token: string): Promise<ClaimResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  return apiCall<ClaimResponse>(() => api.getClaimRaw({ claimNumber }, { signal }))
}

export async function tryGetClaim(claimNumber: string, token: string): Promise<ClaimResponse | null> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.getClaimRaw({ claimNumber }, { signal })
    return (await response.raw.json()) as ClaimResponse
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 404) {
      return null
    }
    return handleError(error)
  }
}

export async function getClaimFolderId(claimNumber: string, token: string): Promise<string | null> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.getClaimFolderIdRaw({ claimNumber }, { signal })
    return await response.value()
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 404) {
      return null
    }
    return handleError(error)
  }
}

export async function getClaimDocuments(folderId: string, token: string): Promise<DocumentInfo[]> {
  const api = createDocumentsApi(token)
  const signal = createTimeoutSignal()
  return apiCall<DocumentInfo[]>(() => api.getDocumentsRaw({ folderId }, { signal }))
}

export async function uploadDocumentToFolder(
  folderId: string,
  file: File,
  token: string,
  cancelSignal?: AbortSignal
): Promise<DocumentInfo> {
  const api = createDocumentsApi(token)
  const signal = createTimeoutSignal(cancelSignal)
  return apiCall<DocumentInfo>(() => api.uploadDocumentRaw({ folderId, file }, { signal }))
}

export async function downloadClaimDocument(id: string, token: string): Promise<Blob> {
  const api = createDocumentsApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.downloadDocumentRaw({ id }, { signal })
    return await response.raw.blob()
  } catch (error) {
    return handleError(error)
  }
}
